import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";

import DstButton from "../../../../components/DstButton/DstButton";
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown/MultiSelectDropdown";
import Popup from "../../../../components/Popup/Popup";
import RequiredLabel from "../../../../components/RequiredLabel/RequiredLabel";
import { useSession } from "../../../../contexts/SessionContext";
import { ProjectSubjectStatusEnum } from "../../../../models/ProjectSubjects";
import translate from "../../../../services/Translate";
import { createProjectSubject, fetchPrograms } from "../../ProjectSubjects.function";
import { CreateProjectSubjectModalProps, ProjectSubjectForm } from "./CreateProjectSubjectModal.d";

const CreateProjectSubjectModal = ({ show, onClose, onCreated }: CreateProjectSubjectModalProps) => {
  const { language } = useSession();

  const [loading, setLoading] = useState(false);

  const initialForm: ProjectSubjectForm = {
    name: "",
    short_name: "",
    description: "",
    language: "",
    difficulty: null,
    status: "",
    program_id: null,
  };

  const [form, setForm] = useState<ProjectSubjectForm>(initialForm);

  const [programs, setPrograms] = useState<any[]>([]);
  const [programsLoading, setProgramsLoading] = useState(true);

  const _handleChange = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const _handleSubmit = () => {
    createProjectSubject(
      {
        name: form.name,
        short_name: form.short_name,
        description: form.description,
        language: form.language,
        difficulty: form.difficulty ? form.difficulty : null,
        status: form.status,
        program_id: form.program_id ? form.program_id : null,
      },
      setLoading,
      () => {
        setForm(initialForm);
        onCreated();
        onClose();
      },
      language
    );
  };

  useEffect(() => {
    fetchPrograms(setPrograms, language, setProgramsLoading);
  }, [language]);

  return (
    <Popup
      show={show}
      onClose={onClose}
      modalTitle={translate(language, "PAGES.PROJECT_SUBJECTS.CREATE_PROJECT.TITLE")}
      size="lg"
      footerContent={
        <React.Fragment>
          <DstButton
            variant="secondary"
            value={translate(language, "GLOBAL.CANCEL")}
            clickFunction={() => {
              setForm(initialForm);
              onClose();
            }}
          />
          <DstButton
            variant="primary"
            value={translate(language, "GLOBAL.CREATE")}
            clickFunction={_handleSubmit}
            disabled={loading || !form.name || !form.short_name || !form.language || !form.status}
          />
        </React.Fragment>
      }
    >
      <Form className="d-flex flex-column gap-3">
        <Form.Group>
          <Form.Label>
            <RequiredLabel label={translate(language, "GLOBAL.NAME")} />
          </Form.Label>
          <Form.Control value={form.name} onChange={(event) => _handleChange("name", event.target.value)} required />
        </Form.Group>
        <Form.Group>
          <Form.Label>
            <RequiredLabel label={translate(language, "PAGES.PROJECT_SUBJECTS.CREATE_PROJECT.SHORT_NAME")} />
          </Form.Label>
          <Form.Control
            value={form.short_name}
            onChange={(event) => _handleChange("short_name", event.target.value)}
            required
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>{translate(language, "GLOBAL.DESCRIPTION")}</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={form.description}
            onChange={(event) => _handleChange("description", event.target.value)}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>
            <RequiredLabel label={translate(language, "GLOBAL.LANGUAGE")} />
          </Form.Label>
          <Form.Select value={form.language} onChange={(event) => _handleChange("language", event.target.value)}>
            <option value="">
              {translate(language, "PAGES.PROJECT_SUBJECTS.CREATE_PROJECT.LANGUAGE.SELECT_OPTION")}
            </option>
            <option value="fr-FR">
              {translate(language, "PAGES.PROJECT_SUBJECTS.CREATE_PROJECT.LANGUAGE.FRENCH")}
            </option>
            <option value="en-US">
              {translate(language, "PAGES.PROJECT_SUBJECTS.CREATE_PROJECT.LANGUAGE.ENGLISH")}
            </option>
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>{translate(language, "PAGES.PROJECT_SUBJECTS.CREATE_PROJECT.DIFFICULTY")}</Form.Label>
          <Form.Control
            type="number"
            min={1}
            max={10}
            step={1}
            value={form.difficulty ?? ""}
            onChange={(e) => {
              if (e.target.value === "") {
                _handleChange("difficulty", null);
                return;
              }
              if (Number(e.target.value) >= 1 && Number(e.target.value) <= 10) {
                _handleChange("difficulty", Number(e.target.value));
              }
            }}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>
            <RequiredLabel label={translate(language, "GLOBAL.STATUS")} />
          </Form.Label>
          <Form.Select value={form.status} onChange={(event) => _handleChange("status", event.target.value)}>
            <option value="">—</option>
            {Object.values(ProjectSubjectStatusEnum).map((status) => (
              <option key={status} value={status}>
                {translate(language, `PAGES.PROJECT_SUBJECTS.STATUS_ENUM.${status}`)}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>{translate(language, "PAGES.PROJECT_SUBJECTS.CREATE_PROJECT.PROGRAM")}</Form.Label>
          <MultiSelectDropdown
            items={programs}
            mock={programsLoading}
            isSingleSelect
            isSingleSelectRemovable
            language={language}
            label={
              form.program_id
                ? programs.find((program) => program.id === Number(form.program_id))?.name
                : translate(language, "PAGES.PROJECT_SUBJECTS.CREATE_PROJECT.SELECT_PROGRAM")
            }
            displayItem={(item) => <span>{item.name}</span>}
            badgeItemProperty="name"
            onConfirm={(selected) => _handleChange("program_id", selected.length ? selected[0].id : "")}
            borderToggle
            btnWidth="100%"
            toggleClass="w-100"
          />
        </Form.Group>
      </Form>
    </Popup>
  );
};

export default CreateProjectSubjectModal;
